'use strict';

const { BaseWidget } = require('./BaseWidget');
const { h } = require('./dom');
const { BX } = require('./theme');
const { maskAadhaar } = require('./format');
const rd = require('./rd');

/**
 * AepsOnboardingWidget — merchant AEPS onboarding with NSDL: Start (kicks off
 * onboarding + auto-sends OTP) -> OTP -> PAN -> Aadhaar -> biometric capture
 * & submit. Mirrors webpayment's components/Vendor/Onboarding/AEPS/Onboard.vue
 * and its useOnboadingApi calls, ported onto client.aeps.onboarding/
 * validateOtp/verifyPan/updateUid/updateBiodetail — the same onboarding
 * sub-routes AepsModule already exposes (aeps.route.js), just not previously
 * wired into a widget (AepsWidget only covers the post-onboarding
 * transaction flow: reqAuth/withdrawal/balance/minStatement/purchase).
 *
 * update_biodetail chains NSDL confirmation + GetAgentDetail server-side on
 * success (aeps.controller.js#update_biodetail), so this widget calls no
 * separate "submit" step — same shape as DmtWidget's Aadhaar onboarding leg.
 *
 * const widget = new AepsOnboardingWidget(client, { onSuccess, onError });
 * widget.mount('#bbx-aeps-onboarding').start();
 */
class AepsOnboardingWidget extends BaseWidget {
  start() {
    this._p = {};
    this.screenStart();
    return this;
  }

  stepSubtitle(n, text) {
    return `Step ${n} of 5 · ${text}`;
  }

  screenStart(errorMsg) {
    this.setHeader({ title: 'AEPS Onboarding', subtitle: this.stepSubtitle(1, 'Get Started'), showBack: false });
    this.showScreen(
      h('div', {}, [
        errorMsg ? this.banner('error', errorMsg) : null,
        h('div', { class: 'bbx-card' }, [
          h('p', {
            class: 'bbx-status-sub',
            style: { margin: 0, textAlign: 'left' },
            text: 'This registers you for Aadhaar-enabled banking services (AEPS). You will need your PAN, Aadhaar number and a connected biometric device to complete verification.',
          }),
        ]),
        h('button', {
          class: 'bbx-btn',
          text: 'Start Onboarding',
          onClick: async (e) => {
            e.target.disabled = true;
            this.renderLoading('Starting onboarding…');
            try {
              const res = await this.client.aeps.onboarding();
              // NSDL's onboarding call can come back with otp_status:true (OTP
              // already sent) even when the outer envelope's statusCode isn't
              // a clean "00" — ported from webpayment's
              // Onboard.vue#handleStep1Continue, which has this exact same
              // two-tier check for the same reason: otp_status/otp_reqid are
              // the fields that reliably survive every response shape this
              // particular call can come back in, statusCode isn't.
              if (res.otp_status === true) {
                this._p.otpReqId = res.data?.otp_reqid || res.otp_reqid || this._p.otpReqId;
                this.screenOtp();
                return;
              }
              this.fail(res, res.message);
              this.screenStart(res.message || 'Could not start onboarding');
            } catch (err) {
              this.fail(err);
              this.screenStart(this.errorMessage(err, 'Could not start onboarding'));
            }
          },
        }),
      ]),
    );
  }

  screenOtp(errorMsg) {
    this.setHeader({ title: 'Verify OTP', subtitle: this.stepSubtitle(2, 'OTP Verification'), showBack: false });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    const otp = this.buildOtpInput({
      onComplete: async (value) => {
        if (!this._p.otpReqId) {
          errorSlot.replaceChildren(this.banner('error', 'OTP request id missing — please restart onboarding'));
          return;
        }
        try {
          const res = await this.client.aeps.validateOtp({ otp: value, otpreqid: this._p.otpReqId });
          if (res.statusCode !== '00') {
            otp.reset();
            errorSlot.replaceChildren(this.banner('error', res.message || 'Incorrect OTP'));
            return;
          }
          this.screenPan();
        } catch (err) {
          otp.reset();
          errorSlot.replaceChildren(this.banner('error', this.errorMessage(err, 'Incorrect OTP')));
        }
      },
      onResend: async () => {
        try {
          const res = await this.client.aeps.requestOtp();
          if (res?.data?.otpreqid) this._p.otpReqId = res.data.otpreqid;
        } catch (err) {
          // transient — surfaced on next submit attempt via the stale otpReqId
        }
      },
    });

    this.showScreen(h('div', {}, [otp.node, errorSlot]));
  }

  screenPan(errorMsg) {
    this.setHeader({ title: 'PAN Verification', subtitle: this.stepSubtitle(3, 'PAN Verification'), showBack: false });

    const panField = this.field({ label: 'PAN Number', placeholder: 'e.g. ABCDE1234F', maxlength: '10', value: this._p.pan || '', onInput: (v) => { this._p.pan = (v || '').toUpperCase(); } });
    const nameField = this.field({ label: 'Full Name (as on PAN)', value: this._p.panName || '', onInput: (v) => { this._p.panName = v; } });
    const dobField = this.field({ label: 'Date of Birth', type: 'date', value: this._p.panDob || '', onInput: (v) => { this._p.panDob = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        panField.node, nameField.node, dobField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Verify & Continue',
          onClick: async (e) => {
            const pan = (this._p.pan || '').toUpperCase();
            if (pan.length !== 10) {
              errorSlot.replaceChildren(this.banner('error', 'Enter a valid 10-character PAN number'));
              return;
            }
            if (!this._p.panName || !this._p.panName.trim()) {
              errorSlot.replaceChildren(this.banner('error', 'Enter the name exactly as on the PAN card'));
              return;
            }
            if (!this._p.panDob) {
              errorSlot.replaceChildren(this.banner('error', 'Enter the date of birth'));
              return;
            }
            // <input type=date> gives yyyy-mm-dd; NSDL's verify_pan wants dd/mm/yyyy
            // (matches webpayment's Onboard.vue#handleStep — see next()'s step===3 branch).
            const [y, m, d] = this._p.panDob.split('-');
            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.renderLoading('Verifying PAN…');
            try {
              const res = await this.client.aeps.verifyPan({ pan, name: this._p.panName.trim().toUpperCase(), dob: `${d}/${m}/${y}` });
              if (res.statusCode !== '00') {
                this.fail(res, res.message);
                this.screenPan(res.message || 'PAN verification failed');
                return;
              }
              this._p.pan = pan;
              this.screenAadhaar();
            } catch (err) {
              this.fail(err);
              this.screenPan(this.errorMessage(err, 'PAN verification failed'));
            }
          },
        }),
      ]),
    );
  }

  screenAadhaar(errorMsg) {
    this.setHeader({ title: 'Aadhaar Verification', subtitle: this.stepSubtitle(4, 'Aadhaar Verification'), showBack: false });

    const aadhaarField = this.field({ label: 'Aadhaar Number', inputmode: 'numeric', maxlength: '12', placeholder: '12-digit Aadhaar', value: this._p.aadhaar || '', onInput: (v) => { this._p.aadhaar = (v || '').replace(/\D/g, '').slice(0, 12); } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        aadhaarField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Verify & Continue',
          onClick: async (e) => {
            if (!this._p.aadhaar || this._p.aadhaar.length !== 12) {
              errorSlot.replaceChildren(this.banner('error', 'Enter a valid 12-digit Aadhaar number'));
              return;
            }
            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.renderLoading('Verifying Aadhaar…');
            try {
              const res = await this.client.aeps.updateUid({ aadhaarno: this._p.aadhaar });
              if (res.statusCode !== '00') {
                this.fail(res, res.message);
                this.screenAadhaar(res.message || 'Aadhaar verification failed');
                return;
              }
              this.screenBiometric();
            } catch (err) {
              this.fail(err);
              this.screenAadhaar(this.errorMessage(err, 'Aadhaar verification failed'));
            }
          },
        }),
      ]),
    );
  }

  screenBiometric(errorMsg) {
    this.setHeader({ title: 'Biometric Verification', subtitle: this.stepSubtitle(5, 'Biometric Verification'), showBack: false });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);
    const statusSlot = h('p', { class: 'bbx-muted' });

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-card' }, [
          h('p', {
            class: 'bbx-status-sub',
            style: { margin: 0, textAlign: 'left' },
            text: `Place the finger linked to Aadhaar ${maskAadhaar(this._p.aadhaar)} on the biometric device to complete verification.`,
          }),
        ]),
        errorSlot,
        statusSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Scan Fingerprint & Submit',
          onClick: async (e) => {
            e.target.disabled = true;
            errorSlot.replaceChildren();

            statusSlot.textContent = 'Connecting to biometric device…';
            const device = await rd.findFirstConnectedDevice();
            if (!device) {
              e.target.disabled = false;
              statusSlot.textContent = '';
              errorSlot.replaceChildren(this.banner('error', 'No RD biometric service found running on this machine'));
              return;
            }

            statusSlot.textContent = 'Place finger on the scanner…';
            const capture = await rd.captureFingerprint(device.vendor);
            if (!capture.ok) {
              e.target.disabled = false;
              statusSlot.textContent = '';
              errorSlot.replaceChildren(this.banner('error', capture.message));
              return;
            }

            statusSlot.textContent = 'Submitting…';
            try {
              const res = await this.client.aeps.updateBiodetail({ PidData: capture.pidDataXml, aadhaarno: this._p.aadhaar });
              statusSlot.textContent = '';
              if (res.statusCode !== '00') {
                this.fail(res, res.message);
                e.target.disabled = false;
                this.screenBiometric(res.message || 'Biometric verification failed');
                return;
              }
              this.screenResult(res);
            } catch (err) {
              statusSlot.textContent = '';
              this.fail(err);
              e.target.disabled = false;
              this.screenBiometric(this.errorMessage(err, 'Biometric verification failed'));
            }
          },
        }),
      ]),
    );
  }

  screenResult(res) {
    this.setHeader({ title: 'AEPS Onboarding', showBack: false });
    const payload = { ...res, pan: this._p.pan, aadhaar: this._p.aadhaar };

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: BX.success2 }, text: '✓' }),
        h('p', { class: 'bbx-status-title', text: 'Onboarding Complete' }),
        h('p', { class: 'bbx-status-sub', text: 'You are now registered for AEPS services.' }),
        h('div', { class: 'bbx-card' }, [
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'PAN' }), h('span', { class: 'bbx-row-v bbx-mono', text: this._p.pan || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Aadhaar' }), h('span', { class: 'bbx-row-v bbx-mono', text: maskAadhaar(this._p.aadhaar) })]),
        ]),
        h('button', { class: 'bbx-btn', text: 'Done', onClick: () => { this.succeed(payload); this.opts.onClose && this.opts.onClose(); } }),
      ]),
    );
  }
}

module.exports = { AepsOnboardingWidget };
